import { useMemo, useCallback } from 'react';
import { GridColDef, GridColumnResizeParams } from '@mui/x-data-grid';
import { useLocalStorageState } from './useLocalStorageState';

interface UseDatagridSettingsOptions<T extends GridColDef> {
  columns: T[];
  storageKey: string;
}

interface DatagridWidthSettings {
  onChange: (params: GridColumnResizeParams) => void;
}

interface UseDatagridSettingsReturn<T extends GridColDef> {
  columns: T[];
  width: DatagridWidthSettings;
}

export const useDatagridSettings = <T extends GridColDef>(
  options: UseDatagridSettingsOptions<T>
): UseDatagridSettingsReturn<T> => {
  const { columns, storageKey } = options;
  const widthStorageKey = `${storageKey}:column-widths`;
  const [columnWidths, setColumnWidths] = useLocalStorageState<Record<string, number>>(widthStorageKey, {});

  const columnsWithWidths = useMemo(
    () =>
      columns.map(col => ({
        ...col,
        width: columnWidths[col.field] ?? col.width
      })) as T[],
    [columns, columnWidths]
  );

  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams): void => {
      setColumnWidths(prev => ({
        ...prev,
        [params.colDef.field]: params.width
      }));
    },
    [setColumnWidths]
  );

  return {
    columns: columnsWithWidths,
    width: {
      onChange: handleColumnWidthChange
    }
  };
};
